import React, { Ref, useEffect, useId, useMemo, useState } from 'react';
import ReactModal from 'react-modal';
import { t } from '@lingui/macro';
import styled from 'styled-components';

import { Close } from '@rover/icons';
import { Button } from '@rover/kibble/core';
import NoScrollController from '@rover/kibble/official-patterns/Modal/utils/NoScrollController';
import { DSTokenMap } from '@rover/kibble/styles';

import type { Props as CommonProps } from './Modal.types';
import StyledModal from './ModalStylingAdapter';
import SearchPerfHackButton from './SearchPerfHackButton';

export const StyledClose = styled(Close)`
  height: 24px;
  width: 24px;
  fill: rgba(0, 0, 0, 0.2);
  &:hover {
    fill: rgba(0, 0, 0, 0.5);
  }
`;

export type Props = CommonProps & {
  aria?: Record<string, any>;
  appElementSelector?: string;
  'aria-labelledby'?: string;
  closeTimeout?: number;
  contentRef?: Ref<any> | null | undefined;
  data?: Record<string, string>;
  disablePortal?: boolean;
  hasCloseButton?: boolean;
  overlayRef?: Ref<any> | null | undefined;
  style?: {
    content?: Record<string, string | number>;
    overlay?: Record<string, string | number>;
  };
  useSearchPerfHack?: boolean;
  className?: string;
};
const noScroll = new NoScrollController();

const DEFAULT_APP_ELEMENT_SELECTOR = '#base-content';
const DEFAULT_CLOSE_TIMEOUT = 150;
const DEFAULT_DISABLE_PORTAL = false;

const Modal = ({
  appElementSelector = DEFAULT_APP_ELEMENT_SELECTOR,
  children,
  closeTimeout = DEFAULT_CLOSE_TIMEOUT,
  disablePortal = DEFAULT_DISABLE_PORTAL,
  hasCloseButton = true,
  isOpen = false,
  onRequestClose = () => {},
  style = undefined,
  useSearchPerfHack = false,
  ...other
}: Props): JSX.Element => {
  // setAppElement has to be called before render or else we get react-modal errors.
  // useMemo with an empty array second arg will run only once, before render, much like componentWillMount does.
  // react-modal's internals make this method safe to call server-side.
  useMemo(() => {
    ReactModal.setAppElement(
      appElementSelector !== undefined ? appElementSelector : DEFAULT_APP_ELEMENT_SELECTOR
    ); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const modalId = useId();
  useEffect(() => {
    if (isOpen) {
      noScroll.on(modalId);
    } else {
      noScroll.off(modalId);
    }

    return () => {
      noScroll.off(modalId);
    };
  }, [isOpen, modalId]);
  // After mount, switch back to the regular button.  This allows onRequestClose to fire as
  // expected once we're out of the mounting/rehyrdrating period during which the hack helps
  const [showPerfHackButton, setShowPerfHackButton] = useState(useSearchPerfHack);
  useEffect(() => setShowPerfHackButton(false), []);
  // ReactModal and ServerModal both take all aria-* props in an `aria` object.
  const ariaProps = other.aria || {};
  Object.keys(other).forEach((key) => {
    if (key.startsWith('aria-')) {
      ariaProps[key.substr(5)] = other[key];
      delete other[key]; // eslint-disable-line no-param-reassign
    }
  });
  return (
    <StyledModal
      {...other}
      closeTimeoutMS={closeTimeout !== undefined ? closeTimeout : DEFAULT_CLOSE_TIMEOUT}
      disablePortal={disablePortal !== undefined ? disablePortal : DEFAULT_DISABLE_PORTAL}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      aria={ariaProps}
      style={style}
      aria-modal
    >
      {showPerfHackButton ? <SearchPerfHackButton /> : null}
      {hasCloseButton && !showPerfHackButton ? (
        <Button
          p="0x"
          aria-label={t`Close modal`}
          variant="flat"
          onClick={() => onRequestClose()}
          data-testid="modal-button-close"
          data-qa-id="modal-button-close"
          sx={{
            position: 'absolute',
            top: ['10px', '42px'],
            right: ['15px', '51px'],
            '&:hover': {
              backgroundColor: DSTokenMap.BACKGROUND_COLOR_PRIMARY.toString(),
            },
          }}
        >
          <StyledClose />
        </Button>
      ) : null}
      {children}
    </StyledModal>
  );
};

export default Modal;
